import { useEffect, useMemo, useState } from "react";
import type { DefinitionTemplateSummary } from "@keydials/api-client";
import type { SegmentNoun } from "@keydials/dial-workspace";
import { api, getAccessToken } from "../auth/AuthContext";
import { builderCopy as copy, fill, nounValues } from "./copy";

/** The dials offered as starting points, by sector (KEY-560). Picking one copies it into a draft of the author's own. */
export function TemplatePicker({
  noun,
  busy,
  onPick,
}: {
  noun: SegmentNoun;
  busy: boolean;
  onPick: (template: DefinitionTemplateSummary) => void;
}) {
  const [templates, setTemplates] = useState<DefinitionTemplateSummary[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [sector, setSector] = useState("");

  useEffect(() => {
    const token = getAccessToken();
    if (!token) return;
    let cancelled = false;
    api
      .listAuthoringTemplates(token)
      .then((list) => {
        if (!cancelled) setTemplates(list);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const sectors = useMemo(
    () => [...new Set((templates ?? []).map((t) => t.industry?.trim()).filter((s): s is string => Boolean(s)))].sort(),
    [templates],
  );

  if (failed) return <p className="kd-builder-muted">{copy.start.template.unavailable}</p>;
  if (!templates) return <p className="kd-builder-muted">{copy.frame.loading}</p>;

  const shown = templates.filter((t) => !sector || t.industry?.trim() === sector);
  const words = nounValues(noun);

  return (
    <div className="kd-template-picker">
      <label className="kd-builder-field kd-template-sector">
        <span>{copy.start.template.sectorLabel}</span>
        <select value={sector} onChange={(e) => setSector(e.target.value)}>
          <option value="">{copy.start.template.allSectors}</option>
          {sectors.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </label>

      {shown.length === 0 ? (
        <p className="kd-builder-muted">{copy.start.template.none}</p>
      ) : (
        <ul className="kd-template-list">
          {shown.map((t) => (
            <li key={t.id}>
              <button type="button" className="kd-choice" disabled={busy} onClick={() => onPick(t)}>
                <span className="kd-choice-title">{t.name}</span>
                {t.description && <span className="kd-choice-body">{t.description}</span>}
                <span className="kd-choice-meta">
                  {[t.industry, fill(t.kind === "Itemised" ? copy.start.checklist.title : copy.start.oneColour.title, words)]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
